/**
 * Shows what U.S. LawShield's promo system said about a CTA's promo code, next to the promo field.
 *
 * INFORMATIONAL ONLY (owner rule 2026-10-25): this component never disables, blocks or changes anything. Some codes
 * exist only in USLS's CRM/AP and not in the platform the API checks, so "not recognised" is a hint, not an error.
 * It has no props that feed back into the form -- it only reads.
 *
 * Why: OKGUNSHOW went unrecognised for most of a show day and "e&eguns" still is; nobody in the CRM could see it.
 */
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "../../hooks/useDebounce";
import {
  describePromoLookup,
  describeRecheck,
  fetchPromoCodeStatus,
  hasUnusualPromoCharacters,
  normalizePromoCode,
  recheckPromoCode,
  type PromoCodeLookup,
} from "../../services/promoLookup.service";

interface Props {
  /** The promo code as currently shown in the field (saved or being typed). */
  code: string | null | undefined;
  /** Show the "Check again" button (people who can edit the CTA). The server checks permission again. */
  canRecheck?: boolean;
  /** Smaller, one-line variant for CTA cards in view mode. */
  compact?: boolean;
}

const RECHECK_COOLDOWN_MS = 60_000;
/** Poll budget per code / per Check again: at most this many status calls (successful OR failed) ... */
const MAX_POLLS = 24;
/** ... and never for longer than this (a backstop; 24 polls at the 30 s pending pace is 12 minutes). */
const MAX_POLL_MS = 15 * 60_000;

/** Attempts so far for a query: successful AND failed refreshes (external audit 4e592725 F2 -- dataUpdateCount alone
 * does not move while the RPC is failing, so an outage polled forever). */
const attemptCount = (
  s: { dataUpdateCount: number; errorUpdateCount: number } | undefined,
) => (s ? s.dataUpdateCount + s.errorUpdateCount : 0);

const toneClass = {
  ok: "text-green-700",
  warn: "text-amber-800",
  muted: "text-gray-500",
} as const;

const PromoCodeLookupStatus: React.FC<Props> = ({
  code,
  canRecheck = false,
  compact = false,
}) => {
  const normalized = normalizePromoCode(code);
  const debounced = useDebounce(normalized, 500);
  const queryClient = useQueryClient();
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [recheckNote, setRecheckNote] = useState<string | null>(null);
  // Synchronous guard: two clicks in the same tick must still make ONE call (state updates are async).
  const lastPressRef = useRef(0);
  // Poll budget counts from the last code change / Check again, not for the whole session (re-audit P3): the
  // attempt count and the time at that moment.
  const pollBaseRef = useRef({ attempts: 0, at: Date.now() });

  // A different code clears the previous code's note and cooldown.
  useEffect(() => {
    setRecheckNote(null);
    setCooldownUntil(0);
    lastPressRef.current = 0;
    pollBaseRef.current = {
      attempts: attemptCount(
        queryClient.getQueryState(["promo-code-lookup", debounced]),
      ),
      at: Date.now(),
    };
  }, [debounced, queryClient]);

  const queryKey = ["promo-code-lookup", debounced];
  const { data, isError, isLoading } = useQuery({
    queryKey,
    enabled: debounced.length > 0 && debounced.length <= 100,
    queryFn: () => fetchPromoCodeStatus(debounced),
    staleTime: 15_000,
    retry: 1,
    retryDelay: 500,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      const row = q.state.data as PromoCodeLookup | null | undefined;
      // Bounded (internal audit P3-2; external audit F2): at most MAX_POLLS attempts -- failed ones count too -- and
      // at most MAX_POLL_MS per code / Check again, then it waits for a new code, a Check again or a reload.
      if (
        !row ||
        attemptCount(q.state) - pollBaseRef.current.attempts >= MAX_POLLS ||
        Date.now() - pollBaseRef.current.at >= MAX_POLL_MS
      ) {
        return false;
      }
      if (row.checking_now) {
        return 5_000;
      }
      if (row.status === "pending") {
        return 30_000;
      }
      return false;
    },
  });

  const recheck = useMutation({
    mutationFn: () => recheckPromoCode(debounced),
    onSuccess: (r) => {
      pollBaseRef.current = {
        attempts: attemptCount(queryClient.getQueryState(queryKey)),
        at: Date.now(),
      };
      setRecheckNote(describeRecheck(r));
      const wait = (r.retry_after_seconds ?? 60) * 1000;
      setCooldownUntil(Date.now() + Math.min(wait, RECHECK_COOLDOWN_MS * 2));
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      setRecheckNote("Couldn't ask again right now.");
      setCooldownUntil(Date.now() + RECHECK_COOLDOWN_MS);
    },
  });

  // Re-render once the cooldown ends so the button comes back.
  const [, tick] = useState(0);
  useEffect(() => {
    if (cooldownUntil <= Date.now()) {
      return;
    }
    const t = setTimeout(
      () => tick((n) => n + 1),
      cooldownUntil - Date.now() + 50,
    );
    return () => clearTimeout(t);
  }, [cooldownUntil]);

  if (!normalized) {
    return null;
  }

  const unusual = hasUnusualPromoCharacters(normalized);
  const stillTyping = normalized !== debounced;

  let tone: keyof typeof toneClass = "muted";
  let text: string;
  if (stillTyping || isLoading) {
    text = "Checking…";
  } else if (isError) {
    text = "Couldn't check right now";
  } else if (!data) {
    text = "";
  } else {
    ({ tone, text } = describePromoLookup(data));
    if (data.checking_now && data.status !== "pending") {
      text = `${text} (checking again…)`;
    }
  }

  const showRecheck =
    canRecheck &&
    !stillTyping &&
    !!data &&
    data.lookup_enabled &&
    !data.checking_now &&
    // Only where a re-check can help (not found / couldn't check / known but no free month). A confirmed free month is
    // never re-armed by hand: a TLS outage during the re-check would drop it on every CTA (internal audit P2-2).
    (data.status === "not_found" ||
      data.status === "error" ||
      (data.status === "live" && !data.thirty_day_promo));
  const coolingDown = cooldownUntil > Date.now();
  let statusAttr: string = data?.status ?? "none";
  if (stillTyping || isLoading) {
    statusAttr = "typing";
  } else if (isError) {
    statusAttr = "rpc_error";
  }

  return (
    <div
      className={compact ? "mt-0.5 text-xs" : "mt-1 text-xs"}
      data-testid="promo-lookup-status"
      data-status={statusAttr}
      aria-live="polite"
    >
      {text && (
        <p className={toneClass[tone]}>
          {tone === "warn" ? "⚠ " : tone === "ok" ? "✓ " : ""}
          {text}
        </p>
      )}
      {unusual && (
        <p className="text-amber-800" data-testid="promo-unusual-chars">
          This code has unusual characters (like &amp; or spaces). Codes like
          this have failed before; check it with U.S. LawShield.
        </p>
      )}
      {(showRecheck || recheckNote) && (
        <div className="flex items-center gap-2 mt-0.5">
          {showRecheck && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const now = Date.now();
                if (coolingDown || recheck.isPending) {
                  return;
                }
                if (now - lastPressRef.current < RECHECK_COOLDOWN_MS) {
                  return;
                }
                lastPressRef.current = now;
                recheck.mutate();
              }}
              disabled={coolingDown || recheck.isPending}
              className="text-primary-600 hover:text-primary-800 underline disabled:text-gray-400 disabled:no-underline"
              data-testid="promo-recheck"
            >
              {recheck.isPending ? "Asking…" : "Check again"}
            </button>
          )}
          {recheckNote && <span className="text-gray-500">{recheckNote}</span>}
        </div>
      )}
    </div>
  );
};

export default PromoCodeLookupStatus;
